import { Adapter } from '@mode-switch/adapt';
import { DataRole } from '@mode-switch/express';
import { Attr, Field, Model, Sort } from '@mode-switch/model';
import { DomainPlan, DomainQuery, PivotBasic, PivotPlan, PivotQuery } from '@mode-switch/query';
import { Encoding } from '@mode-vegas/vegas';

import { buildColorDomainQuery } from './color-domain-query';
import { ModeRole, planPivotQuery, PlanSummary, summarizePivotPlan } from './pivot-query.plan';
import { buildGrandTotalQueries, buildSubtotalQueries } from './pivot-query.totals';

export function buildPivotQuery(
  adapter: Adapter<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  includeColorDomain = false,
  includeSortDomain = false
): PivotPlan<ModeRole> {
  let plan = planPivotQuery(model, viewName, encoding);
  const summary = summarizePivotPlan(plan);

  // Add ByField|ByNested sort domain qeries, if any
  if (includeSortDomain) {
    plan = addOrderedDomainQueries(plan, model, viewName, summary);
  }
  // Mutually exclusive from `includeSortDomain`
  else if (includeColorDomain) {
    plan = addColorDomainQuery(plan, adapter, model, viewName, encoding);
  }

  // Add Grand Total queries, if any
  plan = addGrandTotalPivotQueries(plan, model, viewName, encoding, summary);

  // Add Subtotal queries, if any
  plan = addSubtotalPivotQueries(plan, model, viewName, encoding);

  return plan;
}

function addColorDomainQuery(
  plan: PivotPlan<ModeRole>,
  adapter: Adapter<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding
): PivotPlan<ModeRole> {
  const domainPlan = buildColorDomainQuery(adapter, model, viewName, encoding);
  if (domainPlan != null) {
    plan = DomainPlan.extend(plan, domainPlan);
  }
  return plan;
}

function addOrderedDomainQueries<R extends DataRole>(
  plan: PivotPlan<R>,
  model: Model<R>,
  viewName: string,
  summary: PlanSummary<R>
): PivotPlan<R> {
  const { queries } = summary;

  /**
   * @description Return true if a field is a discrete measure, false otherwise
   */
  function isDiscreteMeasure(field: Field<R>) {
    return field.type === Attr.Type.Discrete && field.role === Attr.Role.Measure;
  }

  /**
   * @description Add a field to the `sortsQueries` map if the field has not
   * already had a sort applied. Discrete measures will put `queries` as-is in
   * the map such that the field is summarized for _each_ query in the domain
   * result; all other field types will add an array with one query: `query`
   */
  function addSort(
    sort: Sort.ByField<R> | Sort.ByNested<R>,
    sortsSeen: Set<string>,
    sortsQueries: Map<Field<R>, PivotQuery.Basic<R>[]>,
    queries: PlanSummary<R>['queries'],
    query: PivotQuery.Basic<R>
  ) {
    let key = sort.field.alias;
    if (sort.type === Sort.Type.ByField) {
      key = `source:${sort.field.formula.source}`;
    }

    if (!sortsSeen.has(key)) {
      sortsSeen.add(key);
      sortsQueries.set(
        sort.field,
        isDiscreteMeasure(sort.field)
          ? // Discrete Measures require one query per series. Each series
            // may have differing sets of dimensions which would summarize
            // the measure differently.. thus 1+ queries per field
            queries.map((q) => q.query)
          : [query]
      );
    }
  }

  ///
  ///

  // Deduped sorts by alias or formula, depending on the sort type
  const sortsSeen = new Set<string>();

  // Map of Field -> Queries to build DomainPlans for
  const sortsQueries = new Map<Field<R>, PivotQuery.Basic<R>[]>();

  for (const { query } of queries) {
    for (const sort of query.sorts ?? []) {
      switch (sort.type) {
        case Sort.Type.ByField:
        case Sort.Type.ByNested: {
          addSort(sort, sortsSeen, sortsQueries, queries, query);
          break;
        }
      }
    }
  }

  // No ByField sorts, bail
  if (sortsQueries.size === 0) {
    return plan;
  }

  let domainPlan: DomainPlan<R> | undefined;
  for (const [field, queries] of Array.from(sortsQueries)) {
    // Create one DomainPlan per query
    // we've collected for this field
    for (const query of queries) {
      const next = DomainPlan.pivot(
        model,
        viewName,
        DomainQuery.Pivot.make(query, {
          fields: [field],
        })
      );

      // Set
      if (domainPlan == null) {
        domainPlan = next;
      }
      // Merge
      else {
        domainPlan = DomainPlan.extend(domainPlan, next);
      }
    }
  }

  if (domainPlan != null) {
    plan = DomainPlan.extend(plan, domainPlan);
  }

  return plan;
}

export function addGrandTotalPivotQueries(
  plan: PivotPlan<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  summary: PlanSummary<ModeRole>
) {
  const grandTotals = buildGrandTotalQueries(model, viewName, encoding, summary);
  for (const gtPlan of grandTotals) {
    plan = PivotBasic.extend(plan, gtPlan);
  }
  return plan;
}

export function buildGrandTotalPivotQueries(
  adapter: Adapter<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding
) {
  const basePlan = planPivotQuery(model, viewName, encoding);
  const summary = summarizePivotPlan(basePlan);
  return buildGrandTotalQueries(model, viewName, encoding, summary);
}

export function buildSubtotalPivotQueries(model: Model<ModeRole>, viewName: string, encoding: Encoding) {
  const basePlan = planPivotQuery(model, viewName, encoding);
  const summary = summarizePivotPlan(basePlan);
  return buildSubtotalQueries(model, viewName, encoding, summary);
}

export function addSubtotalPivotQueries(
  plan: PivotPlan<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding
) {
  const subTotals = buildSubtotalPivotQueries(model, viewName, encoding);
  for (const stPlan of subTotals) {
    plan = PivotBasic.extend(plan, stPlan);
  }
  return plan;
}
